import * as React from 'react';

import { RowSelectionState, Row } from '@tanstack/react-table';

import { DataGridSelection, DataGridSelectionMode } from './DataGrid';

/**
 * Utility functions for selection conversion
 */
export function convertSelectionToRowSelectionState<T>(
  selection: DataGridSelection,
  dataSource: T[],
  rowKey: (row: T) => React.Key,
): RowSelectionState {
  if (selection === 'all') {
    return dataSource.reduce((acc, row) => {
      acc[String(rowKey(row))] = true;
      return acc;
    }, {} as RowSelectionState);
  }

  return Array.from(selection).reduce((acc, key) => {
    acc[String(key)] = true;
    return acc;
  }, {} as RowSelectionState);
}

export function convertRowSelectionStateToSelection(
  rowSelection: RowSelectionState,
): Set<React.Key> {
  return new Set(Object.keys(rowSelection).filter((key) => rowSelection[key]));
}

/**
 * Hook to manage selection state with controlled/uncontrolled support
 */
export function useSelectionState<T>({
  selectedKeys,
  defaultSelectedKeys,
  onSelectionChange,
  selectionMode,
  rowKey,
  dataSource,
  disabledKeys,
  disallowEmptySelection = false,
}: {
  selectedKeys?: DataGridSelection;
  defaultSelectedKeys?: DataGridSelection;
  onSelectionChange?: (keys: DataGridSelection) => void;
  selectionMode: DataGridSelectionMode;
  rowKey: (row: T) => React.Key;
  dataSource: T[];
  disabledKeys?: Set<React.Key>;
  disallowEmptySelection?: boolean;
}) {
  // Determine if controlled
  const isControlled = selectedKeys !== undefined;

  // Internal state for uncontrolled mode
  const [internalSelection, setInternalSelection] =
    React.useState<DataGridSelection>(() => defaultSelectedKeys ?? new Set());

  // Current selection value
  const currentSelection = isControlled ? selectedKeys : internalSelection;

  // Convert Selection to TanStack Table's RowSelectionState format
  const rowSelectionState = React.useMemo(() => {
    if (selectionMode === 'none') return {};
    return convertSelectionToRowSelectionState(
      currentSelection,
      dataSource,
      rowKey,
    );
  }, [currentSelection, dataSource, rowKey, selectionMode]);

  // Selection change handler
  const handleSelectionChange = React.useCallback(
    (
      updaterOrValue:
        | RowSelectionState
        | ((old: RowSelectionState) => RowSelectionState),
    ) => {
      const newRowSelection =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(rowSelectionState)
          : updaterOrValue;

      const newKeys = convertRowSelectionStateToSelection(newRowSelection);

      // Handle disallowEmptySelection
      if (
        disallowEmptySelection &&
        newKeys.size === 0 &&
        currentSelection !== 'all' &&
        (currentSelection instanceof Set ? currentSelection.size > 0 : false)
      ) {
        return; // Don't allow empty selection
      }

      // Handle single selection mode
      if (selectionMode === 'single' && newKeys.size > 1) {
        // Keep only the last selected item
        const lastKey = Array.from(newKeys).pop();
        newKeys.clear();
        if (lastKey !== undefined) {
          newKeys.add(lastKey);
        }
      }

      // Filter out disabled keys
      if (disabledKeys) {
        disabledKeys.forEach((key) => newKeys.delete(key));
      }

      // Update internal state if uncontrolled
      if (!isControlled) {
        setInternalSelection(newKeys);
      }

      // Call the new callback
      if (onSelectionChange) {
        onSelectionChange(newKeys);
      }
    },
    [
      rowSelectionState,
      isControlled,
      onSelectionChange,
      currentSelection,
      disallowEmptySelection,
      selectionMode,
      disabledKeys,
    ],
  );

  // Function to check if a row can be selected
  const getRowCanSelect = React.useCallback(
    (row: Row<T>) => {
      if (selectionMode === 'none') return false;
      const keyFn =
        typeof rowKey === 'function' ? rowKey : (row: T) => row[rowKey];

      const key = String(keyFn(row.original));
      return !disabledKeys?.has(key);
    },
    [disabledKeys, rowKey, selectionMode],
  );

  return {
    rowSelectionState,
    handleSelectionChange,
    currentSelection,
    getRowCanSelect,
  };
}
